import { useState, useEffect } from 'react'
import api from '../services/api'
import SortableTh, { sortRows } from '../components/SortableTh'
import Paginacao, { usePaginacao } from '../components/Paginacao'
import Apolices, { formatMoney } from './Apolices'
import { corClara } from '../utils/cor'
import { escapeHtml } from '../utils/html'
import { useConfig } from '../contexts/ConfigContext'
import IconVisualizar from '../components/icons/IconVisualizar'
import { avisar } from '../components/Avisos'

const statusLabel = (status) => {
  if (status === 'emitido') return { label: 'Emitido', cls: 'badge-green' }
  if (status === 'em_analise') return { label: 'Em Análise', cls: 'badge-blue' }
  if (status === 'pendente') return { label: 'Pendente', cls: 'badge-amber' }
  if (status === 'vencida') return { label: 'Vencida', cls: 'badge-amber' }
  if (status === 'recusada') return { label: 'Recusada', cls: 'badge-gray' }
  if (status === 'cancelada') return { label: 'Cancelada', cls: 'badge-red' }
  return { label: status || '—', cls: 'badge-gray' }
}

const colunasIndicacoes = [
  { key: 'vigencia',   label: 'Vigência\nInicial', get: r => r.data_inicio || '', date: true },
  { key: 'segurado',   label: 'Segurado',          get: r => r.cliente_nome || '' },
  { key: 'ramo',       label: 'Ramo',               get: r => r.ramo || '' },
  { key: 'seguradora', label: 'Seguradora',         get: r => r.seguradora || '' },
  { key: 'premio',     label: 'Prêmio\nTotal',      get: r => parseFloat(r.premio_total || 0), numeric: true },
  { key: 'status',     label: 'Status',             get: r => statusLabel(r.status).label },
  { key: 'indicacao',  label: 'Indicação',          get: r => r.indicacao_nome || '' },
]

// Mesmo padrao de "gerar PDF" ja usado em CRM.jsx: monta uma pagina HTML de impressao numa aba
// nova e chama window.print() — o usuario salva como PDF pelo dialogo de impressao do navegador,
// sem precisar de biblioteca de geracao de PDF no backend.
// Janela de impressao/PDF e um documento novo, sem acesso as CSS custom properties do app —
// recebe a cor de destaque ja resolvida (hex) em vez de var(--primary).
const gerarPdf = (linhas, periodoDe, periodoAte, corPrimaria = '#DD3B00') => {
  const corClaraHex = corClara(corPrimaria)
  const hoje = new Date().toLocaleDateString('pt-BR')
  const periodo = (periodoDe || periodoAte)
    ? `Período: ${periodoDe ? new Date(periodoDe + 'T00:00:00').toLocaleDateString('pt-BR') : 'início'} a ${periodoAte ? new Date(periodoAte + 'T00:00:00').toLocaleDateString('pt-BR') : 'hoje'}`
    : 'Todo o período'

  const linhasHtml = linhas.map((r, i) => {
    const st = statusLabel(r.status)
    const bg = i % 2 === 0 ? '#fff' : '#FAFAFA'
    return `<tr style="background:${bg};">
      <td style="padding:6px 8px;border-bottom:1px solid #eee;">${r.data_inicio ? new Date(r.data_inicio).toLocaleDateString('pt-BR') : '—'}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;font-weight:600;">${escapeHtml(r.cliente_nome)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;">${escapeHtml(r.ramo) || '—'}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;">${escapeHtml(r.seguradora) || '—'}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;text-align:right;">${r.premio_total ? formatMoney(r.premio_total) : '—'}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;">${st.label}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #eee;">${escapeHtml(r.indicacao_nome) || '—'}</td>
    </tr>`
  }).join('')

  const html = `<!DOCTYPE html><html><head>
<meta charset="utf-8"><title>Relatório de Indicações</title>
<style>
  body{font-family:Arial,sans-serif;margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;color:#333;}
  .page{max-width:1000px;margin:0 auto;padding:20px;}
  .hdr{background:${corPrimaria};color:#fff;padding:14px 20px;border-radius:6px;margin-bottom:6px;display:flex;justify-content:space-between;align-items:center;}
  .hdr h1{margin:0;font-size:16px;font-weight:700;}
  .sub{font-size:12px;color:#777;margin-bottom:14px;}
  table{width:100%;border-collapse:collapse;font-size:11px;}
  th{text-align:left;padding:8px;background:${corClaraHex};color:${corPrimaria};font-size:10px;text-transform:uppercase;letter-spacing:.04em;}
  .footer{text-align:center;font-size:10px;color:#aaa;margin-top:16px;border-top:1px solid #eee;padding-top:10px;}
  @media print{@page{size:A4 landscape;margin:10mm;}.page{padding:0;}}
</style></head><body>
<div class="page">
  <div class="hdr"><h1>RELATÓRIO DE INDICAÇÕES</h1><div style="font-size:11px;opacity:.85;">${hoje}</div></div>
  <div class="sub">${periodo} &nbsp;·&nbsp; ${linhas.length} ${linhas.length === 1 ? 'indicação' : 'indicações'}</div>
  <table>
    <thead><tr>
      <th>Vigência Inicial</th><th>Segurado</th><th>Ramo</th><th>Seguradora</th><th style="text-align:right;">Prêmio Total</th><th>Status</th><th>Indicação</th>
    </tr></thead>
    <tbody>${linhasHtml}</tbody>
  </table>
  <div class="footer">Documento gerado pelo sistema Corretora &mdash; ${hoje}</div>
</div>
<script>setTimeout(()=>window.print(),400)</script>
</body></html>`

  const win = window.open('', '_blank')
  if (win) { win.document.write(html); win.document.close() }
  else avisar('Permita pop-ups para gerar o PDF.')
}

export default function Indicacoes() {
  const { config } = useConfig()
  const [linhas, setLinhas] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [periodoDe, setPeriodoDe] = useState('')
  const [periodoAte, setPeriodoAte] = useState('')
  const [sortCol, setSortCol] = useState('vigencia')
  const [sortDir, setSortDir] = useState('desc')
  const [documentoModal, setDocumentoModal] = useState(null)
  const [carregandoVer, setCarregandoVer] = useState(false)
  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  const carregar = () => {
    api.get('/relatorios/indicacoes')
      .then(r => setLinhas(r.data))
      .catch(() => setLinhas([]))
      .finally(() => setCarregando(false))
  }
  useEffect(() => { carregar() }, [])

  const verDocumento = (r) => {
    setCarregandoVer(true)
    api.get(`/apolices/${r.id}`)
      .then(res => setDocumentoModal(res.data))
      .catch(() => setDocumentoModal(r))
      .finally(() => setCarregandoVer(false))
  }

  const filtradas = linhas.filter(r => {
    const vig = r.data_inicio ? r.data_inicio.split('T')[0] : ''
    if (periodoDe && (!vig || vig < periodoDe)) return false
    if (periodoAte && (!vig || vig > periodoAte)) return false
    return true
  })
  const filtradasOrdenadas = sortRows(filtradas, colunasIndicacoes, sortCol, sortDir)
  const paginacao = usePaginacao(filtradasOrdenadas)

  return (
    <>
      {!documentoModal && (<>
      <div className="topbar">
        <h2>Indicações</h2>
      </div>
      <div className="page-content">
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Vigência inicial de</label>
              <input className="form-input" type="date" value={periodoDe} onChange={e => setPeriodoDe(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">até</label>
              <input className="form-input" type="date" value={periodoAte} onChange={e => setPeriodoAte(e.target.value)} />
            </div>
            {(periodoDe || periodoAte) && (
              <button className="btn btn-secondary" onClick={() => { setPeriodoDe(''); setPeriodoAte('') }}>Limpar período</button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <button className="btn btn-primary" onClick={() => gerarPdf(filtradasOrdenadas, periodoDe, periodoAte, config.cor_primaria)} disabled={filtradasOrdenadas.length === 0}>
                Gerar PDF
              </button>
              <span style={{ fontSize: 12, color: '#888' }}>
                {filtradas.length} {filtradas.length === 1 ? 'indicação encontrada' : 'indicações encontradas'}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="table-wrap">
            <table className="table-borda-destaque">
              <thead>
                <tr>
                  {colunasIndicacoes.map(c => (
                    <SortableTh key={c.key} colKey={c.key} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>{c.label}</SortableTh>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginacao.itensPagina.map(r => {
                  const st = statusLabel(r.status)
                  return (
                    <tr key={r.id}>
                      <td style={{ textAlign: 'center' }}>{r.data_inicio ? new Date(r.data_inicio).toLocaleDateString('pt-BR') : '—'}</td>
                      <td style={{ fontWeight: 500 }}>{r.cliente_nome}</td>
                      <td style={{ textAlign: 'center' }}>{r.ramo || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.seguradora || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.premio_total ? formatMoney(r.premio_total) : '—'}</td>
                      <td style={{ textAlign: 'center' }}><span className={`badge ${st.cls}`}>{st.label}</span></td>
                      <td style={{ textAlign: 'center' }}>{r.indicacao_nome || '—'}</td>
                      <td>
                        <button type="button" className="btn btn-secondary btn-sm btn-icon" title="Ver" style={{ color: 'var(--primary)' }} disabled={carregandoVer} onClick={() => verDocumento(r)}><IconVisualizar size={14} /></button>
                      </td>
                    </tr>
                  )
                })}
                {!carregando && filtradasOrdenadas.length === 0 && (
                  <tr><td colSpan={colunasIndicacoes.length + 1} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>Nenhuma indicação encontrada</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Paginacao {...paginacao} rotulo="indicação" rotuloPlural="indicações" />
        </div>
      </div>
      </>)}

      {documentoModal && (
        <Apolices
          modalOnly
          hideNavigation
          voltarLabel="Indicações"
          initialDocument={documentoModal}
          onCloseModal={() => setDocumentoModal(null)}
          onSaved={async () => {
            setDocumentoModal(null)
            await carregar()
          }}
        />
      )}
    </>
  )
}
